import React, { useState } from 'react';
import { Popover, Spinner as ReactSpinner, Table } from 'react-bootstrap';
import { formatAmountsInText } from '../schema/helper';
import RmsPopover from '../common/popover/RmsPopover';
import FinalRiskOverrideModal from './FinalRiskOverrideModal';
import DataFieldTag from '../common/DataFieldTag';
import EndFinalRiskOverrideModal from './EndFinalRiskOverrideModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDoubleRight, faClockRotateLeft } from '@fortawesome/pro-regular-svg-icons';
import Has from '../auth/Has';
import { CAN_ADD_FINAL_RISK_OVERRIDE, CAN_DELETE_FINAL_RISK_OVERRIDE } from '../auth/permissions';
import { InternalApiFinalRiskLevel, InternalApiRisk } from '../api-internal';
import { formatEnumLabel } from '../common/helper';
import './Risk.scss';
import FormattedDateTime from '../common/datetime/FormattedDateTime';
import { useMe } from '../organisation/useMe';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import { useRiskOverrideHistory, usePersonRisks } from './riskActions';
import RiskHistory from './RiskHistory';
import { riskLevelColourStyle } from './riskLevelColours';
import { SalvAccordion, SalvCard, Size } from '@salvapp/ui-components';
import dayjs from 'dayjs';
import { saveRiskHistoryClickEvent } from '../tracking/userEventTracker';

const enumOrder = Object.values(InternalApiFinalRiskLevel);

const getTotalRiskLevelLabel = (totalRiskLevel: number) => {
  if (totalRiskLevel <= 4) {
    return InternalApiFinalRiskLevel.LOW;
  } else if (totalRiskLevel <= 5) {
    return InternalApiFinalRiskLevel.LOW_TO_MEDIUM;
  } else if (totalRiskLevel <= 6) {
    return InternalApiFinalRiskLevel.MEDIUM;
  } else if (totalRiskLevel <= 10) {
    return InternalApiFinalRiskLevel.MEDIUM_TO_HIGH;
  } else {
    return InternalApiFinalRiskLevel.HIGH;
  }
};

const toScore = (risk: InternalApiRisk): number => enumOrder.indexOf(risk.level) * risk.weight;

const getRiskByIndicators = (riskIndicators: InternalApiRisk[]): InternalApiFinalRiskLevel => {
  const totalRiskLevel = riskIndicators.map((i) => toScore(i)).reduce((acc, score) => acc + score, 0);
  return getTotalRiskLevelLabel(totalRiskLevel);
};

interface RiskProps {
  personId: string;
}

export const Risk = ({ personId }: RiskProps) => {
  const { features } = useMe();
  const isFinalRiskScoreEnabled = isFeatureEnabled(ORGANISATION_FEATURE.FINAL_RISK_SCORE, features);
  const isFinalRiskHistoryEnabled = isFeatureEnabled(ORGANISATION_FEATURE.FINAL_RISK_HISTORY, features);
  const [isExpanded, setExpanded] = useState(false);
  const toggleExpansion = async () => {
    setExpanded((prev) => !prev);
    await saveRiskHistoryClickEvent(personId);
  };

  const { data: personRisks, isLoading } = usePersonRisks(personId);
  const { data: overrideHistoryPage } = useRiskOverrideHistory(personId);

  const riskIndicators = personRisks?.risks ?? [];
  const finalRiskLevel = personRisks?.lastFinalRisk?.level ?? getRiskByIndicators(riskIndicators);
  const activeOverrideLevel = personRisks?.activeOverrideLevel;
  const activeOverrideReason = personRisks?.activeOverrideReason;
  const overrideHistory = overrideHistoryPage?.content ?? [];
  const activeFinalRiskLevelStyle = riskLevelColourStyle(activeOverrideLevel ?? finalRiskLevel);
  const lastCalculatedTime = isFinalRiskHistoryEnabled && personRisks?.lastCalculatedTime;
  const lastFinalRisk = isFinalRiskScoreEnabled ? personRisks?.lastFinalRisk : undefined;
  const lastOverrideTime = overrideHistory
    .map((it) => it.endTime ?? it.startTime)
    .reduce((acc, curr) => (acc && acc > curr ? acc : curr), lastFinalRisk?.createdTime);

  const riskOverrideHistoryPopover = (
    <Popover id='risk-override-popover'>
      <Popover.Header as='h3'>Override history</Popover.Header>
      <Popover.Body>
        <Table striped size='sm' className='card-table'>
          <thead>
            <tr>
              <th>From</th>
              <th>To</th>
              <th>Override To</th>
              <th>Override Reason</th>
              <th>Added by</th>
              <th>Ended by</th>
              <th>Comment</th>
            </tr>
          </thead>
          <tbody>
            {overrideHistory.map((risk) => (
              <tr key={`${risk.startTime.toISOString()} - ${risk.level}`}>
                <td>
                  <div>{dayjs(risk.startTime).format('D MMM')}</div>
                  <div className='d-flex justify-content-end date-hint'>{dayjs(risk.startTime).format('HH:mm')}</div>
                </td>
                <td>
                  <div>{(risk.endTime && dayjs(risk.endTime).format('D MMM')) || '--'}</div>
                  <div className='d-flex justify-content-start date-hint'>
                    {risk.endTime && dayjs(risk.endTime).format('HH:mm')}
                  </div>
                </td>
                <td>
                  <div className='small text-uppercase' style={riskLevelColourStyle(risk.level)}>
                    {formatEnumLabel(risk.level)}
                  </div>
                  <div className='date-hint' />
                </td>
                <td>
                  <div>{risk.reason}</div>
                  <div className='date-hint' />
                </td>
                <td>
                  <div>{risk.addedByUserName}</div>
                  <div className='date-hint' />
                </td>
                <td>
                  <div>{risk.endedByUserName || '--'}</div>
                  <div className='date-hint' />
                </td>
                <td className='comment-max-width'>
                  <div>{risk.comment}</div>
                  <div className='date-hint' />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Popover.Body>
    </Popover>
  );

  const popover = (
    <Popover id='risk-popover'>
      <Popover.Header as='h3'>Detailed risk assessment</Popover.Header>
      <Popover.Body>
        <Table striped size='sm' className='card-table'>
          <thead>
            <tr>
              <th>Factor</th>
              <th>Details</th>
              <th>Level</th>
              <th>Score</th>
            </tr>
          </thead>

          <tbody>
            {riskIndicators.map((risk) => {
              return (
                <tr key={risk.id}>
                  <td>{risk.factor}</td>
                  <td>{formatAmountsInText(risk.details)}</td>
                  <td style={riskLevelColourStyle(risk.level)}>
                    <div className='small text-uppercase'>{formatEnumLabel(risk.level)}</div>
                  </td>
                  <td>{toScore(risk)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td className='text-end'>
                <h5 className='mb-0 d-inline'>Risk level</h5>
              </td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span className='text-uppercase' style={riskLevelColourStyle(finalRiskLevel)}>
                    {formatEnumLabel(finalRiskLevel)}
                  </span>
                  {lastFinalRisk && <div style={{ marginLeft: '8px' }}>{lastFinalRisk.score}</div>}
                  {activeOverrideLevel && (
                    <>
                      <FontAwesomeIcon className='mx-2' icon={faAngleDoubleRight} />
                      <span className='text-uppercase' style={riskLevelColourStyle(activeOverrideLevel)}>
                        {formatEnumLabel(activeOverrideLevel)}
                      </span>
                      <DataFieldTag data-testid='override' text='override' />
                    </>
                  )}
                </div>
              </td>
            </tr>
          </tfoot>
        </Table>
      </Popover.Body>
    </Popover>
  );

  return (
    <SalvCard className='risk-card'>
      <SalvCard.Header
        actions={
          <>
            {activeOverrideLevel && (
              <Has allPermissions={CAN_DELETE_FINAL_RISK_OVERRIDE}>
                <EndFinalRiskOverrideModal personId={personId} />
              </Has>
            )}
            <Has allPermissions={CAN_ADD_FINAL_RISK_OVERRIDE}>
              <FinalRiskOverrideModal personId={personId} />
            </Has>
          </>
        }
      >
        Risk assessment
      </SalvCard.Header>
      {isLoading ? (
        <ReactSpinner className='loader-spinner' animation='border' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </ReactSpinner>
      ) : riskIndicators.length === 0 && !activeOverrideLevel ? (
        <SalvCard.Body>
          <p className='risk-panel'>Risk assessment not done yet</p>
        </SalvCard.Body>
      ) : (
        <SalvCard.Body>
          <div className='risk-panel'>
            <div className='col'>
              <div className='row'>
                <h5 className='d-inline'>Risk level</h5>
                {activeOverrideLevel && <DataFieldTag data-testid='overridden' text='overridden' />}
              </div>
              <div className='row'>
                <div className='text-uppercase' style={activeFinalRiskLevelStyle}>
                  {formatEnumLabel(activeOverrideLevel ?? finalRiskLevel)}
                </div>
                {lastFinalRisk && !activeOverrideLevel && (
                  <div style={{ marginLeft: '8px' }}>{lastFinalRisk?.score}</div>
                )}
              </div>
            </div>
            {activeOverrideReason && (
              <div className='col'>
                <div className='row'>
                  <div className='col justify-content-center'>
                    <h5 className='d-inline'>Reason</h5>
                  </div>
                </div>
                <div className='row'>
                  {(overrideHistory.length > 0 || activeOverrideLevel) && (
                    <>
                      <div className='col justify-content-center'>
                        <div className='small'>{activeOverrideReason}</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
            {(lastFinalRisk || lastCalculatedTime) && (
              <div className='col'>
                <div className='row'>
                  {lastFinalRisk && (
                    <div style={{ color: 'var(--text-soft)', fontSize: '14px' }}>
                      • Last changed <FormattedDateTime date={lastOverrideTime} showTime />
                    </div>
                  )}
                </div>
                <div className='row'>
                  {lastCalculatedTime && (
                    <div style={{ color: 'var(--text-soft)', fontSize: '14px' }}>
                      • Last recalculated <FormattedDateTime date={lastCalculatedTime} showTime />
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className='col'>
              <div className='row justify-content-end'>
                <RmsPopover overlay={popover} placement='bottom' dataTestId='detailed-risk-popover'>
                  <span className='hover-link small'>Detailed risk assessment</span>
                </RmsPopover>
              </div>
              {!isFinalRiskHistoryEnabled && (overrideHistory.length > 0 || activeOverrideLevel) && (
                <>
                  <div className='row justify-content-end'>
                    <RmsPopover
                      overlay={riskOverrideHistoryPopover}
                      placement='bottom'
                      dataTestId='override-history-popover'
                    >
                      <span className='hover-link small'>View override history</span>
                    </RmsPopover>
                  </div>
                </>
              )}
            </div>
          </div>
          {isFinalRiskHistoryEnabled && (
            <SalvAccordion
              isExpanded={isExpanded}
              onToggle={toggleExpansion}
              size={Size.SMALL}
              label={
                <div>
                  <FontAwesomeIcon icon={faClockRotateLeft} /> Risk history
                </div>
              }
            >
              <RiskHistory personId={personId} />
            </SalvAccordion>
          )}
        </SalvCard.Body>
      )}
    </SalvCard>
  );
};

export default Risk;
